import React, { useState } from "react";
import {
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  Eye,
  FileText,
  Layers,
  Loader2,
  MessageCircle,
  Package,
  PenLine,
} from "lucide-react";
import AssinaturaPenAnimacao from "./AssinaturaPenAnimacao";
import AssinaturaImagemModal, {
  type AssinaturaImagemVisualizacao,
} from "./AssinaturaImagemModal";

type FichaAssinatura = {
  assinadoConfere?: boolean;
  assinadoProducao?: boolean;
  userConfereNome?: string | null;
  userProducaoNome?: string | null;
  dataAssinaturaConf?: string | null;
  dataAssinaturaProducao?: string | null;
  justificativaSemSobra?: string | null;
  imagemAssinaturaConf?: string | null;
  imagemAssinaturaProducao?: string | null;
};

type AssinaturaSetorCard = {
  setorId: number;
  setorNome: string;
  assinado: boolean;
  userNome?: string | null;
  dataAssinatura?: string | null;
  imagemAssinatura?: string | null;
};

type AssinaturaSetorResumo = {
  setorNome: string;
  assinado: boolean;
  userNome?: string | null;
  dataAssinatura?: string | null;
};

type FichaDiariaAssinaturasProps = {
  ficha?: FichaAssinatura | null;
  setorNome?: string | null;
  assinaturaSetor?: AssinaturaSetorCard | null;
  turnoDia?: boolean;
  podeAssinarConferencia: boolean;
  podeAssinarProducao: boolean;
  podeAssinarProducaoTotal?: boolean;
  prontaParaAssinarConferencia: boolean;
  fichaProntaParaAssinarProducao: boolean;
  motivosConferencia?: string[];
  motivosProducao?: string[];
  assinandoConferencia: boolean;
  assinandoProducao: boolean;
  assinandoProducaoTotal?: boolean;
  onAssinarConferencia: () => void;
  onAssinarProducao: () => void;
  onAssinarProducaoTotal?: () => void;
  pendenciasProducaoTotal?: {
    setoresSemAssinatura: { setorId: number; setorNome: string }[];
    itensPendentes: { setorNome: string; descricao: string }[];
  };
  /** PDF das sobras no card de conferência (noite). */
  mostrarPdfConferencia?: boolean;
  preparandoPdfConferencia?: boolean;
  onVerPdfConferencia?: () => void;
  onWhatsAppConferencia?: () => void;
};

function formatarDataAssinatura(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const AssinaturaAssinadaDetalhe: React.FC<{
  nomeAssinante?: string | null;
  dataAssinatura?: string | null;
  justificativa?: string | null;
  detalheSetores: AssinaturaSetorResumo[];
  podeVerImagem: boolean;
}> = ({
  nomeAssinante,
  dataAssinatura,
  justificativa,
  detalheSetores,
  podeVerImagem,
}) => (
  <div>
    <p className="text-sm font-semibold text-neutral-800">
      {nomeAssinante || "Usuário"}
    </p>
    {dataAssinatura ? (
      <p className="mt-0.5 text-xs text-neutral-500">
        {formatarDataAssinatura(dataAssinatura)}
      </p>
    ) : null}
    {justificativa?.trim() ? (
      <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">
        {justificativa.trim()}
      </p>
    ) : null}
    {podeVerImagem ? (
      <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800">
        <Eye className="h-3.5 w-3.5" aria-hidden />
        Ver assinatura
      </p>
    ) : null}
    {detalheSetores.length > 1 ? (
      <ul className="mt-2 space-y-1 border-t border-emerald-100 pt-2">
        {detalheSetores.map((setor) => (
          <li
            key={setor.setorNome}
            className="flex items-center justify-between gap-2 text-[11px] text-neutral-600"
          >
            <span className="truncate">{setor.setorNome}</span>
            <span className="shrink-0 font-medium text-emerald-700">
              {setor.userNome || "Assinado"}
            </span>
          </li>
        ))}
      </ul>
    ) : null}
  </div>
);

type CardTone = "sky" | "amber" | "violet" | "emerald" | "neutral";

const CARD_TONE: Record<
  CardTone,
  { icon: string; iconBg: string; badge: string; border: string }
> = {
  sky: {
    icon: "text-sky-700",
    iconBg: "bg-sky-50",
    badge: "bg-sky-50 text-sky-800",
    border: "border-sky-200/80",
  },
  amber: {
    icon: "text-amber-800",
    iconBg: "bg-amber-50",
    badge: "bg-amber-50 text-amber-900",
    border: "border-amber-200/80",
  },
  violet: {
    icon: "text-violet-700",
    iconBg: "bg-violet-50",
    badge: "bg-violet-50 text-violet-800",
    border: "border-violet-200/80",
  },
  emerald: {
    icon: "text-emerald-700",
    iconBg: "bg-emerald-50",
    badge: "bg-emerald-50 text-emerald-800",
    border: "border-emerald-200/80",
  },
  neutral: {
    icon: "text-neutral-500",
    iconBg: "bg-neutral-100",
    badge: "bg-neutral-100 text-neutral-600",
    border: "border-neutral-200",
  },
};

type AssinaturaCardProps = {
  titulo: string;
  subtitulo?: string;
  Icon: typeof ClipboardCheck;
  tone: CardTone;
  assinado: boolean;
  assinando: boolean;
  podeAssinar: boolean;
  prontoParaAssinar: boolean;
  aguardandoPreRequisito?: boolean;
  mensagemIndisponivel: string;
  mensagemAguardando?: string;
  nomeAssinante?: string | null;
  dataAssinatura?: string | null;
  justificativa?: string | null;
  onAssinar: () => void;
  onVerImagem?: () => void;
  botaoLabel: string;
  botaoTitle?: string;
  motivos?: string[];
  detalheSetores?: AssinaturaSetorResumo[];
  imagemAssinatura?: string | null;
  acoesExtras?: React.ReactNode;
};

const MAX_MOTIVOS_VISIVEIS = 3;

const AssinaturaCard: React.FC<AssinaturaCardProps> = ({
  titulo,
  subtitulo,
  Icon,
  tone,
  assinado,
  assinando,
  podeAssinar,
  prontoParaAssinar,
  aguardandoPreRequisito,
  mensagemIndisponivel,
  mensagemAguardando,
  nomeAssinante,
  dataAssinatura,
  justificativa,
  onAssinar,
  onVerImagem,
  botaoLabel,
  botaoTitle,
  motivos = [],
  detalheSetores = [],
  imagemAssinatura,
  acoesExtras,
}) => {
  const estilos = CARD_TONE[assinado ? "emerald" : tone];
  const ativo =
    podeAssinar &&
    !assinado &&
    !aguardandoPreRequisito &&
    prontoParaAssinar;
  const motivosVisiveis = motivos.slice(0, MAX_MOTIVOS_VISIVEIS);
  const motivosRestantes = motivos.length - motivosVisiveis.length;
  const podeVerImagem = Boolean(assinado && imagemAssinatura && onVerImagem);

  let statusLabel = "Indisponível";
  if (assinando) statusLabel = "Assinando…";
  else if (assinado) statusLabel = "Assinado";
  else if (ativo) statusLabel = "Pronto para assinar";
  else if (aguardandoPreRequisito) statusLabel = "Aguardando";
  else if (podeAssinar && motivos.length > 0)
    statusLabel = "Ainda não dá para assinar";

  return (
    <article
      className={`flex h-full flex-col rounded-xl border bg-white p-3.5 sm:p-4 ${
        assinado
          ? "border-emerald-200/90 bg-gradient-to-br from-emerald-50/70 to-white"
          : ativo
            ? `${estilos.border} shadow-sm`
            : "border-neutral-200"
      } ${podeVerImagem ? "cursor-pointer hover:border-emerald-300 hover:shadow-sm" : ""}`}
      onClick={podeVerImagem ? onVerImagem : undefined}
      onKeyDown={
        podeVerImagem
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onVerImagem?.();
              }
            }
          : undefined
      }
      role={podeVerImagem ? "button" : undefined}
      tabIndex={podeVerImagem ? 0 : undefined}
      aria-label={
        podeVerImagem
          ? `Ver assinatura de ${nomeAssinante || "usuário"}`
          : undefined
      }
    >
      <div className="flex flex-1 items-start gap-3">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
            assinado ? "bg-emerald-100" : estilos.iconBg
          }`}
        >
          {assinado ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
          ) : (
            <Icon className={`h-5 w-5 ${estilos.icon}`} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-sm font-semibold text-neutral-900">{titulo}</h3>
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                assinado ? CARD_TONE.emerald.badge : estilos.badge
              }`}
            >
              {statusLabel}
            </span>
          </div>
          {subtitulo ? (
            <p className="mt-0.5 text-[11px] leading-relaxed text-neutral-500">
              {subtitulo}
            </p>
          ) : null}

          <div className="mt-2">
            {assinando ? (
              <div className="flex items-center gap-2">
                <AssinaturaPenAnimacao
                  tone={
                    tone === "amber" || tone === "violet"
                      ? "primary"
                      : "emerald"
                  }
                />
                <p className="text-xs font-medium text-neutral-500">
                  Registrando assinatura…
                </p>
              </div>
            ) : assinado ? (
              <AssinaturaAssinadaDetalhe
                nomeAssinante={nomeAssinante}
                dataAssinatura={dataAssinatura}
                justificativa={justificativa}
                detalheSetores={detalheSetores}
                podeVerImagem={podeVerImagem}
              />
            ) : detalheSetores.length > 0 ? (
              <ul className="space-y-1">
                {detalheSetores.map((setor) => (
                  <li
                    key={setor.setorNome}
                    className="flex items-center justify-between gap-2 text-xs text-neutral-600"
                  >
                    <span className="truncate">{setor.setorNome}</span>
                    {setor.assinado ? (
                      <span className="shrink-0 font-medium text-emerald-700">
                        {setor.userNome || "Assinado"}
                      </span>
                    ) : (
                      <span className="shrink-0 text-neutral-400">Pendente</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : motivos.length > 0 ? (
              <ul className="space-y-1">
                {motivosVisiveis.map((motivo) => (
                  <li
                    key={motivo}
                    className="text-xs leading-relaxed text-neutral-600"
                  >
                    {motivo}
                  </li>
                ))}
                {motivosRestantes > 0 ? (
                  <li className="text-[11px] font-medium text-neutral-500">
                    e mais {motivosRestantes}{" "}
                    {motivosRestantes === 1 ? "item" : "itens"}…
                  </li>
                ) : null}
              </ul>
            ) : (
              <p className="text-xs text-neutral-500">Aguardando assinatura</p>
            )}
          </div>
        </div>
      </div>

      {acoesExtras ? (
        <div
          className="mt-auto space-y-2 pt-3"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {acoesExtras}
        </div>
      ) : null}

      {!assinado && !assinando && podeAssinar && !aguardandoPreRequisito ? (
        <button
          type="button"
          disabled={!prontoParaAssinar}
          onClick={(e) => {
            e.stopPropagation();
            onAssinar();
          }}
          className={`${acoesExtras ? "mt-2" : "mt-auto pt-3"} inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white touch-manipulation hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-40`}
          title={botaoTitle}
        >
          <PenLine className="h-4 w-4" />
          {botaoLabel}
        </button>
      ) : null}

      {!assinado &&
      !assinando &&
      (!podeAssinar || aguardandoPreRequisito) ? (
        <p className={`${acoesExtras ? "mt-2" : "mt-auto pt-2.5"} flex items-start gap-1.5 text-[11px] leading-relaxed text-neutral-500`}>
          <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {aguardandoPreRequisito
            ? mensagemAguardando
            : mensagemIndisponivel}
        </p>
      ) : null}
    </article>
  );
};

const FichaDiariaAssinaturas: React.FC<FichaDiariaAssinaturasProps> = ({
  ficha,
  setorNome,
  assinaturaSetor,
  turnoDia,
  podeAssinarConferencia,
  podeAssinarProducao,
  podeAssinarProducaoTotal = false,
  prontaParaAssinarConferencia,
  fichaProntaParaAssinarProducao,
  motivosConferencia,
  motivosProducao,
  assinandoConferencia,
  assinandoProducao,
  assinandoProducaoTotal = false,
  onAssinarConferencia,
  onAssinarProducao,
  onAssinarProducaoTotal,
  pendenciasProducaoTotal,
  mostrarPdfConferencia = false,
  preparandoPdfConferencia = false,
  onVerPdfConferencia,
  onWhatsAppConferencia,
}) => {
  const [visualizacao, setVisualizacao] =
    useState<AssinaturaImagemVisualizacao | null>(null);
  const conferenciaAssinada = ficha?.assinadoConfere === true;
  const producaoAssinada =
    turnoDia && assinaturaSetor
      ? assinaturaSetor.assinado
      : false;
  const producaoTotalAssinada = ficha?.assinadoProducao === true;
  const aguardandoConferencia =
    podeAssinarProducao && !producaoAssinada && !conferenciaAssinada;
  const aguardandoConferenciaTotal =
    podeAssinarProducaoTotal && !producaoTotalAssinada && !conferenciaAssinada;
  const mostrarProducaoSetor = turnoDia && Boolean(setorNome || assinaturaSetor);
  const mostrarProducaoTotal =
    turnoDia &&
    (pendenciasProducaoTotal != null ||
      podeAssinarProducaoTotal ||
      producaoTotalAssinada);
  const pendenciasTotalMotivos = [
    ...(pendenciasProducaoTotal?.setoresSemAssinatura ?? []).map(
      (s) => `Setor ${s.setorNome} ainda não assinou.`
    ),
    ...(pendenciasProducaoTotal?.itensPendentes ?? []).map(
      (i) => `${i.setorNome}: ${i.descricao} sem produção/justificativa.`
    ),
  ];
  const tituloConferencia = turnoDia
    ? "Conferência"
    : "Responsável pela Conferência";
  const tituloProducao = turnoDia && setorNome
    ? `Produção — ${setorNome}`
    : turnoDia
      ? "Produção do setor"
      : "Produção do setor";
  const nomeProducao = assinaturaSetor?.userNome ?? null;
  const dataProducao = assinaturaSetor?.dataAssinatura ?? null;
  const imagemConferencia = ficha?.imagemAssinaturaConf ?? null;
  const imagemProducaoSetor = assinaturaSetor?.imagemAssinatura ?? null;
  const imagemProducaoTotal = ficha?.imagemAssinaturaProducao ?? null;
  const qtdCards =
    1 + (mostrarProducaoSetor ? 1 : 0) + (mostrarProducaoTotal ? 1 : 0);
  const gridCols =
    qtdCards >= 3
      ? "sm:grid-cols-2 lg:grid-cols-3"
      : qtdCards === 2
        ? "sm:grid-cols-2"
        : "";

  const acoesPdfConferencia =
    mostrarPdfConferencia && onVerPdfConferencia ? (
      <div className="space-y-2">
        <button
          type="button"
          disabled={preparandoPdfConferencia}
          onClick={() => onVerPdfConferencia()}
          className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl border border-sky-300 bg-sky-50 px-4 text-sm font-semibold text-sky-900 touch-manipulation hover:bg-sky-100 disabled:opacity-60"
        >
          {preparandoPdfConferencia ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <FileText className="h-4 w-4" />
          )}
          Ver PDF das sobras
        </button>
        {onWhatsAppConferencia ? (
          <button
            type="button"
            disabled={preparandoPdfConferencia}
            onClick={() => onWhatsAppConferencia()}
            className="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 text-xs font-medium text-neutral-600 touch-manipulation hover:border-neutral-300 hover:bg-neutral-50 disabled:opacity-60"
          >
            {preparandoPdfConferencia ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />
            )}
            Encaminhar no WhatsApp (opcional)
          </button>
        ) : null}
      </div>
    ) : null;

  return (
    <section
      className="mx-auto mt-5 w-full max-w-5xl"
      aria-label="Assinaturas da ficha"
    >
      <h2 className="mb-2.5 text-sm font-semibold text-neutral-800">
        Assinaturas
      </h2>
      <div className={`grid gap-2.5 ${gridCols}`}>
        <AssinaturaCard
          titulo={tituloConferencia}
          subtitulo="Assinatura da loja, não por setor"
          Icon={ClipboardCheck}
          tone="sky"
          assinado={conferenciaAssinada}
          assinando={assinandoConferencia}
          podeAssinar={podeAssinarConferencia}
          prontoParaAssinar={prontaParaAssinarConferencia}
          nomeAssinante={ficha?.userConfereNome}
          dataAssinatura={ficha?.dataAssinaturaConf}
          justificativa={ficha?.justificativaSemSobra}
          imagemAssinatura={imagemConferencia}
          onAssinar={onAssinarConferencia}
          onVerImagem={
            imagemConferencia
              ? () =>
                  setVisualizacao({
                    titulo: "Assinatura — Conferência",
                    imagem: imagemConferencia,
                    nome: ficha?.userConfereNome,
                    data: ficha?.dataAssinaturaConf,
                  })
              : undefined
          }
          botaoLabel="Assinar conferência"
          botaoTitle={
            !prontaParaAssinarConferencia
              ? "Complete SOBRA em todos os itens ou deixe todos em branco"
              : undefined
          }
          mensagemIndisponivel="Assinatura de conferência indisponível nesta data ou turno."
          motivos={motivosConferencia}
          acoesExtras={acoesPdfConferencia}
        />
        {mostrarProducaoSetor ? (
        <AssinaturaCard
          titulo={tituloProducao}
          subtitulo={
            setorNome
              ? `Somente quem produz o setor ${setorNome}`
              : "Assinatura de produção por setor"
          }
          Icon={Package}
          tone="amber"
          assinado={producaoAssinada}
          assinando={assinandoProducao}
          podeAssinar={podeAssinarProducao}
          prontoParaAssinar={fichaProntaParaAssinarProducao}
          aguardandoPreRequisito={aguardandoConferencia}
          nomeAssinante={nomeProducao}
          dataAssinatura={dataProducao}
          imagemAssinatura={imagemProducaoSetor}
          onAssinar={onAssinarProducao}
          onVerImagem={
            imagemProducaoSetor
              ? () =>
                  setVisualizacao({
                    titulo: setorNome
                      ? `Assinatura — Produção ${setorNome}`
                      : "Assinatura — Produção do setor",
                    imagem: imagemProducaoSetor,
                    nome: nomeProducao,
                    data: dataProducao,
                  })
              : undefined
          }
          botaoLabel="Assinar produção do setor"
          botaoTitle={
            !fichaProntaParaAssinarProducao
              ? "Conclua todos os itens do setor (produzido, não produzido pela unidade ou justificativa) e encerre os cronômetros"
              : undefined
          }
          mensagemIndisponivel="Assinatura de produção disponível no turno DIA após a conferência, para quem produz neste setor."
          mensagemAguardando="Assine a conferência antes de assinar a produção."
          motivos={turnoDia ? motivosProducao : undefined}
        />
        ) : null}
        {mostrarProducaoTotal ? (
          <AssinaturaCard
            titulo="Produção total dos setores"
            subtitulo="Supervisor de produção. Pode assinar mesmo com setores ou itens pendentes. Os setores ainda podem assinar depois."
            Icon={Layers}
            tone="amber"
            assinado={producaoTotalAssinada}
            assinando={assinandoProducaoTotal}
            podeAssinar={podeAssinarProducaoTotal}
            prontoParaAssinar={podeAssinarProducaoTotal}
            aguardandoPreRequisito={aguardandoConferenciaTotal}
            nomeAssinante={ficha?.userProducaoNome}
            dataAssinatura={ficha?.dataAssinaturaProducao}
            imagemAssinatura={imagemProducaoTotal}
            onAssinar={() => onAssinarProducaoTotal?.()}
            onVerImagem={
              imagemProducaoTotal
                ? () =>
                    setVisualizacao({
                      titulo: "Assinatura — Produção total dos setores",
                      imagem: imagemProducaoTotal,
                      nome: ficha?.userProducaoNome,
                      data: ficha?.dataAssinaturaProducao,
                    })
                : undefined
            }
            botaoLabel="Assinar produção setores"
            mensagemIndisponivel="Apenas o supervisor de produção assina a produção total dos setores."
            mensagemAguardando="Assine a conferência antes de assinar a produção total."
            motivos={
              !producaoTotalAssinada && pendenciasTotalMotivos.length > 0
                ? pendenciasTotalMotivos
                : undefined
            }
          />
        ) : null}
      </div>
      <AssinaturaImagemModal
        visualizacao={visualizacao}
        onClose={() => setVisualizacao(null)}
      />
    </section>
  );
};

export default FichaDiariaAssinaturas;
